import { create } from 'zustand';
import { isAxiosError } from 'axios';
import { useCases } from '../../infrastructure/di/container';
import { getErrorMessage } from '../../infrastructure/http/common';
import { TelegramAccountEntity } from '../../domain/entities/TelegramAccount';
import {
  TelegramCallbackAnswerEntity,
  TelegramDialogEntity,
  TelegramDialogMessageEntity,
  TelegramInboxSettingsEntity,
  TelegramJoinStatus,
  TelegramJoinTargetEntity,
  TelegramMessageButtonEntity,
  TelegramPeerCardEntity,
  dialogFromPeerCard,
  isTelegramSearchQueryValid,
  mergeTelegramMessages,
  newestTelegramMessageId,
  oldestTelegramMessageId,
  telegramDialogKey,
} from '../../domain/entities/TelegramMessenger';

const ACCOUNTS_PAGE_SIZE = 100;
export const MESSAGES_PAGE_SIZE = 50;

const toErrorSlug = (e: unknown): string => {
  if (isAxiosError(e)) return getErrorMessage(e);
  if (e instanceof Error && e.message) return e.message;
  return 'unknown-error';
};

export interface RefreshMessagesOptions {
  full?: boolean;
  force?: boolean;
}

interface AdminMessengerState {
  accounts: TelegramAccountEntity[];
  accountUuid?: string;
  dialogs: TelegramDialogEntity[];
  dialogKey?: string;
  messages: TelegramDialogMessageEntity[];
  hasMoreMessages: boolean;
  settings?: TelegramInboxSettingsEntity;
  isLoadingAccounts: boolean;
  isLoadingDialogs: boolean;
  isLoadingMessages: boolean;
  isSending: boolean;
  isSavingSettings: boolean;
  searchQuery: string;
  searchResults: TelegramPeerCardEntity[];
  isSearching: boolean;
  error?: string;
  loadAccounts: () => Promise<void>;
  selectAccount: (uuid?: string) => void;
  loadDialogs: (silent?: boolean) => Promise<void>;
  selectDialog: (dialogKey?: string) => void;
  loadMessages: () => Promise<void>;
  loadOlderMessages: () => Promise<void>;
  refreshMessages: (options?: RefreshMessagesOptions) => Promise<void>;
  send: (text: string) => Promise<void>;
  loadSettings: () => Promise<void>;
  saveSettings: (settings: TelegramInboxSettingsEntity) => Promise<void>;
  search: (query: string) => Promise<void>;
  clearSearch: () => void;
  openPeer: (card: TelegramPeerCardEntity) => string;
  join: (target: TelegramJoinTargetEntity) => Promise<{ status: TelegramJoinStatus; dialogKey?: string }>;
  pressButton: (messageId: string, button: TelegramMessageButtonEntity) => Promise<TelegramCallbackAnswerEntity>;
  reset: () => void;
}

const initial = {
  accounts: [],
  accountUuid: undefined,
  dialogs: [],
  dialogKey: undefined,
  messages: [],
  hasMoreMessages: false,
  settings: undefined,
  isLoadingAccounts: false,
  isLoadingDialogs: false,
  isLoadingMessages: false,
  isSending: false,
  isSavingSettings: false,
  searchQuery: '',
  searchResults: [],
  isSearching: false,
  error: undefined,
};

export const useAdminMessengerStore = create<AdminMessengerState>((set, get) => {
  const selectedDialog = () => get().dialogs.find((d) => telegramDialogKey(d) === get().dialogKey);
  let refreshesInFlight = 0;

  return {
    ...initial,
    async loadAccounts() {
      set({ isLoadingAccounts: true });
      try {
        const result = await useCases.listTelegramAccounts().execute({ page: 1, perPage: ACCOUNTS_PAGE_SIZE, search: '' });
        set({ accounts: result.items, isLoadingAccounts: false });
      } catch (e) {
        set({ isLoadingAccounts: false, error: toErrorSlug(e) });
      }
    },
    selectAccount(uuid) {
      if (get().accountUuid === uuid) return;
      set({
        accountUuid: uuid,
        dialogs: [],
        dialogKey: undefined,
        messages: [],
        hasMoreMessages: false,
        settings: undefined,
        searchQuery: '',
        searchResults: [],
        isSearching: false,
        error: undefined,
      });
    },
    async loadDialogs(silent = false) {
      const accountUuid = get().accountUuid;
      if (!accountUuid) return;
      if (!silent) set({ isLoadingDialogs: true, error: undefined });
      try {
        const loaded = await useCases.listTelegramDialogs().execute(accountUuid);
        if (get().accountUuid !== accountUuid) return;
        const opened = selectedDialog();
        const keepOpened = opened && !loaded.some((d) => telegramDialogKey(d) === telegramDialogKey(opened));
        set({ dialogs: keepOpened ? [opened, ...loaded] : loaded, isLoadingDialogs: false });
      } catch (e) {
        if (get().accountUuid !== accountUuid) return;
        set({ isLoadingDialogs: false, error: silent ? get().error : toErrorSlug(e) });
      }
    },
    selectDialog(dialogKey) {
      if (get().dialogKey === dialogKey) return;
      set({ dialogKey, messages: [], hasMoreMessages: false });
    },
    async loadMessages() {
      const { accountUuid, dialogKey } = get();
      const dialog = selectedDialog();
      if (!accountUuid || !dialog) return;
      set({ isLoadingMessages: true, error: undefined });
      try {
        const messages = await useCases.listTelegramDialogMessages().execute(accountUuid, dialog);
        if (get().accountUuid !== accountUuid || get().dialogKey !== dialogKey) return;
        set({ messages, hasMoreMessages: messages.length >= MESSAGES_PAGE_SIZE, isLoadingMessages: false });
      } catch (e) {
        if (get().accountUuid !== accountUuid || get().dialogKey !== dialogKey) return;
        set({ isLoadingMessages: false, error: toErrorSlug(e) });
      }
    },
    async loadOlderMessages() {
      const { accountUuid, dialogKey, messages } = get();
      const dialog = selectedDialog();
      const offsetId = oldestTelegramMessageId(messages);
      if (!accountUuid || !dialog || !offsetId) return;
      set({ isLoadingMessages: true, error: undefined });
      try {
        const older = await useCases.listTelegramDialogMessages().execute(accountUuid, dialog, { offsetId });
        if (get().accountUuid !== accountUuid || get().dialogKey !== dialogKey) return;
        set((state) => ({
          messages: [...state.messages, ...older],
          hasMoreMessages: older.length >= MESSAGES_PAGE_SIZE,
          isLoadingMessages: false,
        }));
      } catch (e) {
        if (get().accountUuid !== accountUuid || get().dialogKey !== dialogKey) return;
        set({ isLoadingMessages: false, error: toErrorSlug(e) });
      }
    },
    async refreshMessages({ full = false, force = false } = {}) {
      const { accountUuid, dialogKey, isLoadingMessages, messages } = get();
      const dialog = selectedDialog();
      if (!accountUuid || !dialog || isLoadingMessages) return;
      if (!force && refreshesInFlight > 0) return;
      const minId = full ? undefined : newestTelegramMessageId(messages);
      refreshesInFlight += 1;
      try {
        const latest = await useCases.listTelegramDialogMessages().execute(accountUuid, dialog, minId ? { minId } : undefined);
        if (get().accountUuid !== accountUuid || get().dialogKey !== dialogKey || get().isLoadingMessages) return;
        if (minId && latest.length >= MESSAGES_PAGE_SIZE) {
          set({ messages: latest, hasMoreMessages: true });
          return;
        }
        set((state) => ({
          messages: mergeTelegramMessages(state.messages, latest),
          hasMoreMessages: state.messages.length ? state.hasMoreMessages : latest.length >= MESSAGES_PAGE_SIZE,
        }));
      } catch {
        return;
      } finally {
        refreshesInFlight -= 1;
      }
    },
    async send(text) {
      const accountUuid = get().accountUuid;
      const dialog = selectedDialog();
      if (!accountUuid || !dialog) return;
      set({ isSending: true });
      try {
        await useCases.sendTelegramMessage().execute(accountUuid, dialog, text);
      } catch (e) {
        throw new Error(toErrorSlug(e));
      } finally {
        set({ isSending: false });
      }
      await Promise.all([get().refreshMessages({ full: true, force: true }), get().loadDialogs(true)]);
    },
    async loadSettings() {
      const accountUuid = get().accountUuid;
      if (!accountUuid) return;
      try {
        const settings = await useCases.getTelegramInboxSettings().execute(accountUuid);
        if (get().accountUuid === accountUuid) set({ settings });
      } catch (e) {
        set({ error: toErrorSlug(e) });
      }
    },
    async saveSettings(settings) {
      const accountUuid = get().accountUuid;
      if (!accountUuid) return;
      set({ isSavingSettings: true });
      try {
        set({ settings: await useCases.updateTelegramInboxSettings().execute(accountUuid, settings) });
      } catch (e) {
        throw new Error(toErrorSlug(e));
      } finally {
        set({ isSavingSettings: false });
      }
    },
    async search(query) {
      const accountUuid = get().accountUuid;
      if (!accountUuid) return;
      if (!isTelegramSearchQueryValid(query)) {
        set({ searchQuery: query, searchResults: [], isSearching: false });
        return;
      }
      set({ searchQuery: query, isSearching: true });
      try {
        const searchResults = await useCases.searchTelegramPeers().execute(accountUuid, query);
        if (get().accountUuid !== accountUuid || get().searchQuery !== query) return;
        set({ searchResults, isSearching: false });
      } catch (e) {
        if (get().accountUuid !== accountUuid || get().searchQuery !== query) return;
        set({ searchResults: [], isSearching: false, error: toErrorSlug(e) });
      }
    },
    clearSearch() {
      set({ searchQuery: '', searchResults: [], isSearching: false });
    },
    openPeer(card) {
      const key = telegramDialogKey(card);
      if (!get().dialogs.some((d) => telegramDialogKey(d) === key)) {
        set((state) => ({ dialogs: [dialogFromPeerCard(card), ...state.dialogs] }));
      }
      get().selectDialog(key);
      return key;
    },
    async join(target) {
      const accountUuid = get().accountUuid;
      if (!accountUuid) throw new Error('unknown-error');
      let result;
      try {
        result = await useCases.joinTelegramPeer().execute(accountUuid, target);
      } catch (e) {
        throw new Error(toErrorSlug(e));
      }
      if (get().accountUuid !== accountUuid || result.status === 'request_pending') return { status: result.status };
      const card = result.peer ?? (result.status === 'joined' && 'peer' in target ? target.peer : undefined);
      const joined = card && result.status === 'joined' ? { ...card, joined: true } : card;
      const dialogKey = joined ? get().openPeer(joined) : undefined;
      set((state) => ({
        searchResults: state.searchResults.map((c) => (dialogKey && telegramDialogKey(c) === dialogKey ? { ...c, joined: true } : c)),
      }));
      await get().loadDialogs(true);
      return { status: result.status, dialogKey };
    },
    async pressButton(messageId, button) {
      const accountUuid = get().accountUuid;
      const dialog = selectedDialog();
      if (!accountUuid || !dialog) throw new Error('unknown-error');
      let answer;
      try {
        answer = await useCases.pressTelegramInlineButton().execute(accountUuid, dialog, messageId, button);
      } catch (e) {
        throw new Error(toErrorSlug(e));
      }
      await get().refreshMessages({ full: true, force: true });
      return answer;
    },
    reset() {
      set(initial);
    },
  };
});
